import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Pencil, Trash2 } from "lucide-react";
import PageWrapper from "../layout/PageWrapper";
import Button from "../forms/Button";
import ConfirmModal from "../modals/ConfirmModal";
import Toast from "../ui/Toast";
import useToast from "../../hooks/useToast";
import { apiErrorMessage } from "../../api/taskBoardService";
import { BACKLOG_STATUS, STATUS_LABELS, TASK_STATUSES } from "./constants";
import { ErrorState, SectionCard, SkeletonRows } from "./shared";

/**
 * Shared detail page shell for a project / squad / sprint: back link, admin
 * actions (edit, delete), task summary and a board link
 * pre-filtered by the entity id.
 */
const CatalogDetailLayout = ({
  backTo,
  backLabel,
  title,
  titleAddon,
  subtitle,
  loading,
  error,
  onRetry,
  admin,
  onEdit,
  onDelete,
  entityLabel,
  boardFilter,
  taskCounts,
  children,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast, showError, hideToast } = useToast();

  const run = async (fn) => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      showError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Product Backlog tasks are drafts, not part of any project/squad/sprint
  // summary, so they are left out of the counts.
  const statuses = TASK_STATUSES.filter((s) => s.value !== BACKLOG_STATUS);
  const total = statuses.reduce((sum, s) => sum + (taskCounts?.[s.value] || 0), 0);

  return (
    <PageWrapper
      title={
        <span className="inline-flex items-center gap-3 flex-wrap">
          {title}
          {titleAddon}
        </span>
      }
      subtitle={subtitle}
      actions={
        admin &&
        !loading &&
        !error && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={onEdit} disabled={busy}>
              <Pencil className="w-4 h-4" /> Edit
            </Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={busy}>
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          </div>
        )
      }
    >
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm mb-4 text-gray-600 dark:text-light-400 hover:text-primary-accent">
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </Link>

      {error ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : loading ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4 min-w-0">{children}</div>
          <div className="space-y-4">
            <SectionCard
              title="Tasks"
              actions={
                <Link to={`/task-board?${boardFilter}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary-accent hover:underline">
                  <LayoutGrid className="w-3.5 h-3.5" /> View on board
                </Link>
              }
            >
              <p className="text-2xl font-bold text-gray-900 dark:text-light-100">{total}</p>
              <p className="text-xs text-gray-500 dark:text-light-500 mb-3">tasks in this {entityLabel}</p>
              <ul className="space-y-1 text-sm">
                {statuses.map((s) => (
                  <li key={s.value} className="flex items-center justify-between text-gray-600 dark:text-light-400">
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${s.accent}`} />
                      {STATUS_LABELS[s.value]}
                    </span>
                    <span className="font-medium">{taskCounts?.[s.value] || 0}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => run(onDelete)}
        title={`Delete ${entityLabel}?`}
        message={`This permanently deletes the ${entityLabel}. It is only allowed when no task uses it.`}
        confirmText="Delete permanently"
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </PageWrapper>
  );
};

export default CatalogDetailLayout;
